import React, { useState, useCallback, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TargetSyncProvider } from './contexts/TargetSyncContext';
import { Toaster } from './components/ui/sonner';
import Layout from './components/Layout';
import CommandPalette from './components/CommandPalette';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ResearchPage from './pages/ResearchPage';
import PipelinePage from './pages/PipelinePage';
import TargetsPage from './pages/TargetsPage';
import TargetDetailPage from './pages/TargetDetailPage';
import ProjectsPage from './pages/ProjectsPage';
import OutreachPage from './pages/OutreachPage';
import ContextImportPage from './pages/ContextImportPage';
import DeduplicationPage from './pages/DeduplicationPage';
import GrantsPage from './pages/GrantsPage';
import EventsPage from './pages/EventsPage';
import OrgProfilePage from './pages/OrgProfilePage';
import AdminPage from './pages/AdminPage';
import CognitiveProfilePage from './pages/CognitiveProfilePage';
import './App.css';

// Protected Route wrapper
const ProtectedRoute = ({ children, onOpenCommandPalette }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-2 border-[#00D4C8]/30 border-t-[#00D4C8] rounded-full animate-spin" />
          <p className="text-[#A3B5C7] text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <Layout onOpenCommandPalette={onOpenCommandPalette}>{children}</Layout>;
};

// Public Route wrapper (redirects to dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-[#00D4C8]/30 border-t-[#00D4C8] rounded-full animate-spin" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppRoutes() {
  const [cmdPaletteOpen, setCmdPaletteOpen] = useState(false);
  const { user } = useAuth();
  const openPalette = useCallback(() => setCmdPaletteOpen(true), []);

  // Global Cmd+K listener
  useEffect(() => {
    const handleKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCmdPaletteOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  return (
    <>
      {user && (
        <CommandPalette
          open={cmdPaletteOpen}
          onClose={() => setCmdPaletteOpen(false)}
        />
      )}
      <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute onOpenCommandPalette={openPalette}>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/research"
        element={
          <ProtectedRoute onOpenCommandPalette={openPalette}>
            <ResearchPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pipeline"
        element={
          <ProtectedRoute onOpenCommandPalette={openPalette}>
            <PipelinePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/outreach"
        element={
          <ProtectedRoute onOpenCommandPalette={openPalette}>
            <OutreachPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/targets"
        element={
          <ProtectedRoute onOpenCommandPalette={openPalette}>
            <TargetsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/targets/:id"
        element={
          <ProtectedRoute onOpenCommandPalette={openPalette}>
            <TargetDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects"
        element={
          <ProtectedRoute onOpenCommandPalette={openPalette}>
            <ProjectsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/context-import"
        element={
          <ProtectedRoute onOpenCommandPalette={openPalette}>
            <ContextImportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/deduplication"
        element={
          <ProtectedRoute onOpenCommandPalette={openPalette}>
            <DeduplicationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/grants"
        element={
          <ProtectedRoute onOpenCommandPalette={openPalette}>
            <GrantsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/events"
        element={
          <ProtectedRoute onOpenCommandPalette={openPalette}>
            <EventsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/org-profile"
        element={
          <ProtectedRoute onOpenCommandPalette={openPalette}>
            <OrgProfilePage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/thinking-model"
        element={
          <ProtectedRoute onOpenCommandPalette={openPalette}>
            <CognitiveProfilePage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/admin"
        element={
          <ProtectedRoute onOpenCommandPalette={openPalette}>
            <AdminPage />
          </ProtectedRoute>
        }
      />

      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <TargetSyncProvider>
            <AppRoutes />
            <Toaster position="top-right" />
          </TargetSyncProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
